import { useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useClinicStaff } from '@/hooks/useClinicStaff';
import { useReassignOwnership } from '@/hooks/useReassignOwnership';
import { useOwnershipHistory } from '@/hooks/useOwnershipHistory';
import { useIsAdmin } from '@/hooks/useUserRole';
import { UserCog, History, ArrowRight, Loader2 } from 'lucide-react';
import type { PolicyDetail } from '@/hooks/usePolicy';

interface OwnerReassignmentProps {
  policy: PolicyDetail;
}

export function OwnerReassignment({ policy }: OwnerReassignmentProps) {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: staffMembers = [], isLoading: staffLoading } = useClinicStaff();
  const { data: history = [], isLoading: historyLoading } = useOwnershipHistory(policy.id);
  const reassignMutation = useReassignOwnership();
  
  const [selectedOwner, setSelectedOwner] = useState<string>('');

  const handleReassign = () => {
    if (!selectedOwner || selectedOwner === policy.policy_owner) return;
    
    reassignMutation.mutate({
      policyId: policy.id,
      previousOwnerId: policy.policy_owner,
      newOwnerId: selectedOwner,
    }, {
      onSuccess: () => {
        setSelectedOwner('');
      }
    });
  };

  if (adminLoading || staffLoading) {
    return <Skeleton className="h-[200px]" />;
  }

  // Only show to admins
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserCog className="h-4 w-4" />
            Reassign Policy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            <p>Currently Assigned To: <span className="font-medium text-foreground">{policy.owner_profile?.full_name ?? 'Unknown'}</span></p>
          </div>

          {(() => {
            const eligibleOwners = staffMembers.filter(
              s => s.id !== policy.policy_owner && (s.role === 'owner' || s.role === 'admin')
            );
            
            if (eligibleOwners.length === 0) {
              return (
                <p className="text-sm text-muted-foreground italic">
                  No other owners or admins available to reassign to.
                </p>
              );
            }

            return (
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={selectedOwner} onValueChange={setSelectedOwner}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select new assignee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleOwners.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.name} ({staff.role.charAt(0).toUpperCase() + staff.role.slice(1)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleReassign}
                  disabled={!selectedOwner || reassignMutation.isPending}
                >
                  {reassignMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Reassigning...
                    </>
                  ) : (
                    'Reassign'
                  )}
                </Button>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Assignment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No ownership changes recorded.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Changed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((change) => (
                    <TableRow key={change.id}>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(change.changed_at), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">
                            {change.previous_owner_profile?.full_name ?? 'None'}
                          </span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium">
                            {change.new_owner_profile?.full_name ?? 'Unknown'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {change.changed_by_profile?.full_name ?? 'Unknown'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
